<template>
  <div>
    <!-- Loading State -->
    <div v-if="videoDevicesLoading" class="mt-4">
      <v-row>
        <v-col
          v-for="n in 9"
          :key="`video-skeleton-${n}`"
          :cols="gridConfig.mobile"
          :sm="gridConfig.tablet"
          :lg="gridConfig.desktop"
          class="px-1 py-1"
        >
          <v-skeleton-loader type="image" />
        </v-col>
      </v-row>
    </div>

    <!-- No Results State -->
    <div
      v-else-if="videoDevices.length === 0"
      class="text-center grey--text mt-4"
      height="230px"
    >
      No video cameras available for selected facilities
    </div>

    <!-- Results State -->
    <div v-else class="mt-4">
      <!-- Grid Info Display (for debugging/info) -->
      <div v-if="showGridInfo" class="mb-2 text-center">
        <v-chip small color="info" text-color="white">
          {{ videoDevices.length }} cameras - {{ gridConfig.columns }} columns -
          {{ cardHeight }}px height
        </v-chip>
      </div>

      <v-row class="px-3 pb-3">
        <v-col
          v-for="(device, index) in videoDevices"
          :key="index"
          :cols="gridConfig.mobile"
          :sm="gridConfig.tablet"
          :lg="gridConfig.desktop"
          class="px-1 py-1"
        >
          <v-card
            elevation="2"
            class="video-card rounded-lg"
            @click="openVideoDialog(device)"
            :style="{ height: cardHeight + 'px' }"
          >
            <!-- Header with camera info and status -->
            <div class="video-header-overlay">
              <div class="header-left">
                <div class="camera-name-container">
                  <v-icon
                    left
                    :color="
                      deviceAuthStatus[device.externalId]?.online
                        ? 'success'
                        : 'error'
                    "
                    class="mr-2"
                    x-small
                    >mdi-circle</v-icon
                  >

                  <span class="camera-name">{{ device.zoneName }}</span>
                  <!-- Info Icon with Tooltip -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="white"
                        class="ml-1 info-icon"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="showDeviceInfo(device)"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <div class="camera-tooltip">
                      <div>
                        <strong>{{ device.zoneName }}</strong>
                      </div>
                      <div>Device ID: {{ device.shortId }}</div>
                      <div>External ID: {{ device.externalId }}</div>
                      <div>Facility: {{ device.facilityName }}</div>
                      <div>
                        Status:
                        {{
                          deviceAuthStatus[device.externalId]?.online
                            ? "Online"
                            : "Offline"
                        }}
                      </div>
                    </div>
                  </v-tooltip>
                </div>
              </div>
              <div class="header-right">
                <!-- Fullscreen Icon -->
                <v-btn
                  icon
                  x-small
                  class="ml-1 fullscreen-btn"
                  @click.stop="openVideoDialog(device)"
                  title="Open Fullscreen"
                >
                  <v-icon small color="white">mdi-fullscreen</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Live Preview Container with EEN SDK -->
            <div
              class="video-container position-relative rounded-lg"
              :style="{
                height: videoContainerHeight + 'px',
                backgroundColor: '#000',
              }"
            >
              <!-- Loading indicator -->
              <v-progress-circular
                v-if="!deviceAuthStatus[device.externalId]?.ready"
                indeterminate
                color="primary"
                class="video-loader"
              />

              <!-- Live Video Preview using EEN SDK -->
              <div
                v-if="
                  deviceAuthStatus[device.externalId]?.ready &&
                  !deviceAuthStatus[device.externalId]?.error
                "
                class="live-preview-container"
              >
                <!-- EEN Live Video Container -->
                <div
                  :id="`een_preview_${device.externalId}`"
                  class="een-video-preview"
                  @click.stop="openVideoDialog(device)"
                ></div>

                <!-- Refresh overlay (appears on hover) -->
                <div class="preview-controls">
                  <v-btn
                    icon
                    small
                    color="white"
                    @click.stop="refreshEENPreview(device.externalId)"
                    title="Refresh Preview"
                    class="refresh-btn"
                  >
                    <v-icon small>mdi-refresh</v-icon>
                  </v-btn>
                </div>

                <!-- Play overlay (center) -->
                <div class="play-overlay" @click.stop="openVideoDialog(device)">
                  <v-btn icon color="white" class="play-button">
                    <v-icon large>mdi-play-circle</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Error State -->
              <div
                v-if="deviceAuthStatus[device.externalId]?.error"
                class="error-state"
              >
                <v-icon large color="error">mdi-video-off</v-icon>
                <div class="error-text mt-2">
                  <div class="subtitle-2">
                    {{
                      deviceAuthStatus[device.externalId]?.online
                        ? "Preview Unavailable"
                        : "Camera Offline"
                    }}
                  </div>
                  <div class="caption">{{ device.zoneName }}</div>
                </div>
                <v-btn
                  small
                  color="primary"
                  class="mt-2"
                  @click.stop="retryPreview(device)"
                >
                  Retry
                </v-btn>
              </div>

              <div class="video-time-overlay">
                <div class="camera-time">{{ getCurrentTime() }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-dialog v-model="videoDialog" fullscreen hide-overlay>
      <v-card>
        <v-card-title class="headline">
          <v-toolbar-title>
            <v-icon
              left
              small
              class="mb-1"
              :color="currentViewMode === 'live' ? 'success' : 'warning'"
            >
              {{ currentViewMode === "live" ? "mdi-circle" : "mdi-history" }}
            </v-icon>
            {{ currentVideoDevice?.zoneName }} -
            {{ currentVideoDevice?.facilityName }} -
            {{ currentViewMode === "live" ? "Live Video" : "Playback" }}
          </v-toolbar-title>

          <v-spacer />

          <!-- View Mode Toggle -->
          <div class="view-mode-toggle mr-4">
            <v-btn-toggle v-model="currentViewMode" dense mandatory>
              <v-btn value="live" small>
                <v-icon left color="success" small>mdi-circle</v-icon>
                Live
              </v-btn>
              <v-btn value="playback" small>
                <v-icon left small color="warning">mdi-history</v-icon>
                Playback
              </v-btn>
            </v-btn-toggle>
          </div>

          <!-- Camera Navigation -->
          <div class="camera-nav-buttons mr-4">
            <v-btn
              icon
              :disabled="!canSwitchToPrevious"
              title="Previous Camera"
              @click="switchToPreviousCamera"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-chip color="info" text-color="white" small class="mx-2">
              {{ currentCameraIndex + 1 }} / {{ videoDevices.length }}
            </v-chip>
            <v-btn
              icon
              :disabled="!canSwitchToNext"
              title="Next Camera"
              @click="switchToNextCamera"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <v-btn icon @click="closeVideoDialog">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0 fullscreen-container">
          <div class="video-player-wrapper">
            <!-- Live Video View -->
            <div
              v-show="currentViewMode === 'live'"
              class="video-view-container"
            >
              <!-- EEN SDK Video Player -->
              <div
                v-if="currentVideoDevice"
                :id="`een_fullscreen_${currentVideoDevice.externalId}`"
                :key="`fullscreen_${currentVideoDevice.externalId}`"
                class="een-player-container"
              ></div>

              <!-- Live Loading state -->
              <div v-if="fullscreenLoading" class="fullscreen-loading">
                <v-progress-circular indeterminate color="primary" size="64" />
                <div class="mt-4">
                  Loading Live Video - {{ currentVideoDevice?.zoneName }}...
                </div>
              </div>

              <!-- Live Error state -->
              <div v-if="playerError" class="fullscreen-error">
                <v-icon large color="warning">mdi-alert-circle</v-icon>
                <div class="mt-2">{{ playerError }}</div>
                <v-btn
                  color="primary"
                  class="mt-2"
                  @click="retryFullscreenPlayer"
                >
                  Retry Live
                </v-btn>
              </div>
            </div>

            <!-- Playback Video View -->
            <div
              v-show="currentViewMode === 'playback'"
              class="video-view-container"
            >
              <div v-if="authStatus.value" class="iframe-container">
                <iframe
                  v-if="showIframe"
                  id="sd_vms_iframe"
                  width="100%"
                  height="100%"
                  title="History Browser"
                  :src="iframeSrc"
                  allowfullscreen
                  @load="setupIframeListener"
                  frameborder="0"
                  class="fullscreen-iframe"
                ></iframe>
              </div>
              <div v-else class="text-center py-4 fullscreen-loading">
                <v-progress-circular indeterminate color="primary" size="64" />
                <div class="mt-2 white--text">Loading authentication...</div>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { EEN_AUTH_STATUS } from "../../fixtures/graphql";
import { GET_ALL_ZONE_DEVICES } from "./graphql";
import useFacility from "../../authentication/useFacility";
import LivePlayer from "@een/live-video-web-sdk";

export default {
  name: "EnterpriseVideoGrid",
  props: {
    selectedFacilities: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { currentFacilityId, facilityTimeZone } = useFacility();
    return { currentFacilityId, facilityTimeZone };
  },
  data() {
    return {
      videoDevicesLoading: true,
      videoDevices: [],
      videoDialog: false,
      currentVideoDevice: null,
      selectedDevice: null,
      authStatus: { value: false, accessToken: null },
      deviceAuthStatus: {},
      previewIntervals: {},
      playerError: null,
      currentTime: new Date().toLocaleTimeString(),
      refreshTimeouts: {},
      fullscreenLoading: false,
      currentCameraIndex: 0,
      eenPreviewPlayers: {},
      eenFullscreenPlayer: null,
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
      showGridInfo: false,
      currentViewMode: "live",
      showIframe: false,
    };
  },
  computed: {
    canSwitchToPrevious() {
      return this.currentCameraIndex > 0;
    },
    canSwitchToNext() {
      return this.currentCameraIndex < this.videoDevices.length - 1;
    },

    // Skeleton height for fullscreen grid
    skeletonHeight() {
      // Calculate based on available fullscreen height
      const availableHeight = this.windowHeight - 56; // minus header height
      return Math.floor(availableHeight / 2) - 8; // 2 rows with some padding
    },

    gridConfig() {
      const count = this.videoDevices.length;
      const isDesktop = this.windowWidth >= 1264;
      const isTablet = this.windowWidth >= 600 && this.windowWidth < 1264;
      const isMobile = this.windowWidth < 600;

      const availableHeight = this.windowHeight - 120;

      let config = {
        columns: 3,
        desktop: 4,
        tablet: 6,
        mobile: 12,
      };

      if (isDesktop) {
        if (count <= 3) {
          config = { columns: 3, desktop: 4, tablet: 6, mobile: 12 };
        } else if (count <= 6) {
          config = { columns: 3, desktop: 4, tablet: 6, mobile: 12 };
        } else if (count <= 8) {
          config = { columns: 4, desktop: 3, tablet: 6, mobile: 12 };
        } else if (count <= 12) {
          config = { columns: 4, desktop: 3, tablet: 6, mobile: 12 };
        } else if (count <= 15) {
          config = { columns: 5, desktop: 2.4, tablet: 4, mobile: 12 };
        } else if (count <= 18) {
          config = { columns: 6, desktop: 2, tablet: 4, mobile: 12 };
        } else {
          config = { columns: 6, desktop: 2, tablet: 4, mobile: 12 };
        }
      } else if (isTablet) {
        if (count <= 6) {
          config = { columns: 2, desktop: 4, tablet: 6, mobile: 12 };
        } else if (count <= 9) {
          config = { columns: 3, desktop: 4, tablet: 4, mobile: 12 };
        } else {
          config = { columns: 3, desktop: 4, tablet: 4, mobile: 12 };
        }
      } else {
        config = { columns: 1, desktop: 4, tablet: 6, mobile: 12 };
      }

      return config;
    },

    cardHeight() {
      const count = this.videoDevices.length;
      const columns = this.gridConfig.columns;
      const availableHeight = this.windowHeight - 150;

      const optimalRows = Math.ceil(count / columns);
      const maxRowsOnScreen = Math.floor(availableHeight / 200);

      let targetRows = Math.min(optimalRows, Math.max(2, maxRowsOnScreen));

      let height = Math.floor(availableHeight / targetRows) - 32;

      if (height < 180) height = 180;
      if (height > 300) height = 300;

      if (this.windowWidth < 600) {
        height = Math.min(300, height);
      } else if (this.windowWidth < 1264) {
        height = Math.min(280, height);
      }

      return height;
    },

    videoContainerHeight() {
      return this.cardHeight;
    },

    iframeSrc() {
      return this.currentVideoDevice
        ? `https://iframe.eagleeyenetworks.com/#/historylive/${this.currentVideoDevice.externalId}`
        : "";
    },
  },
  watch: {
    selectedFacilities: {
      immediate: true,
      handler() {
        this.fetchVideoDevices();
      },
      deep: true,
    },
    currentViewMode: {
      async handler(newMode, oldMode) {
        if (newMode !== oldMode) {
          if (newMode === "live" && this.currentVideoDevice) {
            this.cancel();
            this.$nextTick(async () => {
              await this.initializeFullscreenPlayer();
            });
          } else if (newMode === "playback") {
            await this.cleanupFullscreenPlayer();
            this.fetchAuthenticationStatus();
          }
        }
      },
    },

    currentVideoDevice: {
      handler(newDevice, oldDevice) {
        if (newDevice && newDevice.externalId !== oldDevice?.externalId) {
          this.cancel();

          if (this.currentViewMode === "live") {
            this.initializeFullscreenPlayer();
          } else if (this.currentViewMode === "playback") {
            this.fetchAuthenticationStatus();
          }
        }
      },
    },
  },
  mounted() {
    setInterval(this.updateCurrentTime, 1000);
    window.addEventListener("resize", this.handleResize);
  },

  async beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);

    // Fix: Cleanup all video frames first
    for (const deviceId in this.eenPreviewPlayers) {
      try {
        const videoElement = document.getElementById(`een_preview_${deviceId}`);
        if (videoElement) {
          const video = videoElement.querySelector("video");
          if (video) {
            this.cleanupVideoFrames(video);
          }
        }
        await this.eenPreviewPlayers[deviceId].stop();
      } catch (error) {
        console.error(`Error stopping preview for device ${deviceId}:`, error);
      }
    }

    await this.cleanupFullscreenPlayer();
    this.cancel();

    Object.values(this.previewIntervals).forEach((interval) => {
      clearInterval(interval);
    });

    Object.values(this.refreshTimeouts).forEach((timeout) => {
      clearTimeout(timeout);
    });

    // Fix: Force cleanup any remaining video elements
    this.$el.querySelectorAll("video").forEach((video) => {
      this.cleanupVideoFrames(video);
    });
  },

  methods: {
    // `devices` accepts only a single `parentId`, so each selected facility is
    // queried on its own and the results are merged. Scoping by the default org
    // alone would miss cameras in facilities under a cross-region manager's other
    // regions, so the fetch is rooted at every selected facility instead.
    async fetchVideoDevices() {
      try {
        this.videoDevicesLoading = true;
        this.videoDevices = [];

        const selectedFacilityIds = (this.selectedFacilities || []).map(
          (facility) => facility.id
        );
        if (!selectedFacilityIds.length) {
          this.videoDevicesLoading = false;
          return;
        }

        const results = await Promise.all(
          selectedFacilityIds.map((parentId) =>
            this.$apollo.query({
              query: GET_ALL_ZONE_DEVICES,
              variables: { parentId, type: "een" },
              fetchPolicy: "network-only",
            })
          )
        );

        const devices = results.flatMap(({ data }) => data?.devices || []);
        const selectedFacilityIdSet = new Set(selectedFacilityIds);
        const filteredDevices = devices.filter(
          (device) =>
            device.type === "een" &&
            device.subtype === "video_camera" &&
            selectedFacilityIdSet.has(device.organization?.ancestors?.[0]?.id)
        );

        // Add enhanced device information
        this.videoDevices = filteredDevices.map((device) => ({
          ...device,
          zoneName: device.organization?.name || "Unknown Zone",
          facilityName:
            device.organization?.ancestors?.[0]?.name || "Unknown Facility",
          facilityId: device.organization?.ancestors?.[0]?.id,
        }));

        this.initializeDeviceAuth();
      } catch (error) {
        console.error("Error fetching video devices:", error);
      } finally {
        this.videoDevicesLoading = false;
      }
    },

    handleResize() {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
    },

    async initializeDeviceAuth() {
      // Group devices by their facility IDs
      const facilityIds = [
        ...new Set(
          this.videoDevices
            .map((device) => device.organization?.ancestors?.[0]?.id)
            .filter(Boolean)
        ),
      ];

      // Get auth tokens for each facility
      for (const facilityId of facilityIds) {
        try {
          const response = await this.$apollo.query({
            query: EEN_AUTH_STATUS,
            variables: { organization_id: facilityId }, // Use specific facility ID
          });

          const authData = response.data.eenAuthStatus;
          if (authData.accessToken) {
            // Store tokens per facility
            this.authStatus = {
              value: true,
              accessToken: authData.accessToken,
            };

            // Update devices for this facility
            this.videoDevices
              .filter(
                (device) =>
                  device.organization?.ancestors?.[0]?.id === facilityId
              )
              .forEach((device) => {
                this.$set(this.deviceAuthStatus, device.externalId, {
                  ready: true,
                  accessToken: authData.accessToken,
                  error: null,
                  online: true,
                });
              });
          }
        } catch (error) {
          console.error(`Auth failed for facility ${facilityId}:`, error);
          // Mark devices as failed for this facility
          this.videoDevices
            .filter(
              (device) => device.organization?.ancestors?.[0]?.id === facilityId
            )
            .forEach((device) => {
              this.$set(this.deviceAuthStatus, device.externalId, {
                ready: false,
                accessToken: null,
                error: "Authentication failed",
                online: false,
              });
            });
        }
      }

      this.$nextTick(() => {
        this.startAllEENPreviews();
      });
    },

    async startAllEENPreviews() {
      for (const device of this.videoDevices) {
        await this.createEENPreview(device.externalId);
      }
    },

    async createEENPreview(deviceId) {
      if (this.eenPreviewPlayers[deviceId]) return;

      try {
        await this.$nextTick();

        const videoElement = document.getElementById(`een_preview_${deviceId}`);
        if (!videoElement) {
          console.warn(`Video element not found for device ${deviceId}`);
          return;
        }

        let video = videoElement.querySelector("video");
        if (!video) {
          video = document.createElement("video");
          video.autoplay = true;
          video.muted = true;
          video.playsInline = true;
          video.controls = false;
          video.style.width = "100%";
          video.style.height = "100%";
          video.style.objectFit = "cover";

          // Fix: Add frame cleanup for preview videos too
          this.setupVideoFrameCleanup(video);

          videoElement.appendChild(video);
        }

        const device = this.videoDevices.find((d) => d.externalId === deviceId);
        const config = {
          videoElement: video,
          cameraId: deviceId,
          baseUrl: "https://api.c000.eagleeyenetworks.com",
          jwt: this.deviceAuthStatus[deviceId]?.accessToken,
        };

        const player = new LivePlayer();
        this.eenPreviewPlayers[deviceId] = player;

        video.addEventListener("loadstart", () => {
          console.log(`Preview loading for device ${deviceId}`);
        });

        video.addEventListener("canplay", () => {
          this.$set(this.deviceAuthStatus[deviceId], "error", null);
          this.$set(this.deviceAuthStatus[deviceId], "online", true);
          console.log(`Preview started for device ${deviceId}`);
        });

        video.addEventListener("error", (error) => {
          console.error(`Preview error for device ${deviceId}:`, error);
          this.cleanupVideoFrames(video); // Fix: Cleanup on error
          this.$set(
            this.deviceAuthStatus[deviceId],
            "error",
            "Preview unavailable"
          );
          this.$set(this.deviceAuthStatus[deviceId], "online", false);
        });

        // Fix: Add cleanup on stream end
        video.addEventListener("ended", () => {
          this.cleanupVideoFrames(video);
        });

        try {
          await player.start(config);
        } catch (sdkError) {
          console.error(`SDK Error for device ${deviceId}:`, sdkError);
          if (config.baseUrl.includes("api.c000")) {
            console.log(`Retrying with login URL for device ${deviceId}`);
            config.baseUrl = "https://login.eagleeyenetworks.com";
            await player.start(config);
          } else {
            throw sdkError;
          }
        }
      } catch (error) {
        console.error(
          `Failed to create preview for device ${deviceId}:`,
          error
        );
        this.$set(
          this.deviceAuthStatus[deviceId],
          "error",
          "Failed to load preview"
        );
        this.$set(this.deviceAuthStatus[deviceId], "online", false);
        this.fallbackToImagePreview(deviceId);
      }
    },
    fallbackToImagePreview(deviceId) {
      const videoElement = document.getElementById(`een_preview_${deviceId}`);
      if (!videoElement) return;

      videoElement.innerHTML = "";

      const img = document.createElement("img");
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.objectFit = "cover";
      img.alt = "Camera Preview";

      img.src = this.getLivePreviewUrl(deviceId);

      img.onload = () => {
        this.$set(this.deviceAuthStatus[deviceId], "error", null);
        this.$set(this.deviceAuthStatus[deviceId], "online", true);
      };

      img.onerror = () => {
        this.$set(
          this.deviceAuthStatus[deviceId],
          "error",
          "Preview unavailable"
        );
        this.$set(this.deviceAuthStatus[deviceId], "online", false);
      };

      videoElement.appendChild(img);

      if (this.previewIntervals[deviceId]) {
        clearInterval(this.previewIntervals[deviceId]);
      }

      this.previewIntervals[deviceId] = setInterval(() => {
        img.src = this.getLivePreviewUrl(deviceId);
      }, 5000);
    },

    getLivePreviewUrl(deviceId) {
      const token =
        this.deviceAuthStatus[deviceId]?.accessToken ||
        this.authStatus.accessToken;
      if (!token) return "";

      const timestamp = Date.now();
      return `https://login.eagleeyenetworks.com/asset/asset/image.jpeg?id=${deviceId}&A=${token}&timestamp=${timestamp}&size=640x480&quality=med&_cb=${Math.random()}`;
    },

    retryPreview(device) {
      this.$set(this.deviceAuthStatus[device.externalId], "error", null);
      this.$set(this.deviceAuthStatus[device.externalId], "online", true);
      this.refreshEENPreview(device.externalId);
    },

    showDeviceInfo(device) {
      this.selectedDevice = device;
    },

    async openVideoDialog(device) {
      await this.cleanupFullscreenPlayer();
      this.cancel();

      this.currentVideoDevice = device;
      this.currentCameraIndex = this.videoDevices.findIndex(
        (d) => d.externalId === device.externalId
      );
      this.playerError = null;
      this.fullscreenLoading = true;
      this.currentViewMode = "live";

      this.videoDialog = true;

      this.$nextTick(async () => {
        await this.initializeFullscreenPlayer();
      });
    },

    async closeVideoDialog() {
      await this.cleanupFullscreenPlayer();
      this.cancel();
      this.videoDialog = false;
      this.currentVideoDevice = null;
      this.playerError = null;
      this.fullscreenLoading = false;
      this.currentCameraIndex = 0;
      this.currentViewMode = "live";
    },

    async switchToPreviousCamera() {
      if (this.canSwitchToPrevious) {
        const newIndex = this.currentCameraIndex - 1;
        await this.switchToCamera(newIndex);
      }
    },

    async switchToNextCamera() {
      if (this.canSwitchToNext) {
        const newIndex = this.currentCameraIndex + 1;
        await this.switchToCamera(newIndex);
      }
    },

    async switchToCamera(index) {
      if (index >= 0 && index < this.videoDevices.length) {
        await this.cleanupFullscreenPlayer();
        this.cancel();

        this.currentCameraIndex = index;
        this.currentVideoDevice = this.videoDevices[index];
        this.fullscreenLoading = true;
        this.playerError = null;

        this.$forceUpdate();

        this.$nextTick(async () => {
          if (this.currentViewMode === "live") {
            await this.initializeFullscreenPlayer();
          } else if (this.currentViewMode === "playback") {
            this.fetchAuthenticationStatus();
          }
        });
      }
    },

    async initializeFullscreenPlayer() {
      const deviceToken =
        this.deviceAuthStatus[this.currentVideoDevice?.externalId]?.accessToken;

      if (!this.currentVideoDevice || !deviceToken) {
        this.fullscreenLoading = false;
        this.playerError = "Authentication token not available for this camera";
        return;
      }

      try {
        this.fullscreenLoading = true;
        await new Promise((resolve) => setTimeout(resolve, 100));

        const container = document.getElementById(
          `een_fullscreen_${this.currentVideoDevice.externalId}`
        );
        if (!container) {
          console.error("Fullscreen container not found");
          this.fullscreenLoading = false;
          return;
        }

        container.innerHTML = "";

        const video = document.createElement("video");
        video.autoplay = true;
        video.muted = false;
        video.playsInline = true;
        video.controls = true;
        video.style.width = "100%";
        video.style.height = "100%";
        video.style.objectFit = "contain";
        video.style.backgroundColor = "#000";

        // Fix: Add proper frame cleanup handling
        this.setupVideoFrameCleanup(video);

        container.appendChild(video);

        const config = {
          videoElement: video,
          cameraId: this.currentVideoDevice.externalId,
          baseUrl: "https://api.c000.eagleeyenetworks.com",
          jwt: deviceToken,
        };

        this.eenFullscreenPlayer = new LivePlayer();

        video.addEventListener("canplay", () => {
          this.playerError = null;
          this.fullscreenLoading = false;
          console.log(
            `Fullscreen video started for ${this.currentVideoDevice.zoneName}`
          );
        });

        video.addEventListener("error", (error) => {
          console.error("Fullscreen video error:", error);
          this.playerError = "Failed to load video stream";
          this.fullscreenLoading = false;
        });

        video.addEventListener("loadstart", () => {
          console.log(
            `Loading fullscreen video for ${this.currentVideoDevice.zoneName}`
          );
        });

        // Fix: Add proper cleanup on video end
        video.addEventListener("ended", () => {
          this.cleanupVideoFrames(video);
        });

        try {
          await this.eenFullscreenPlayer.start(config);
        } catch (sdkError) {
          console.error("Fullscreen SDK Error:", sdkError);
          if (config.baseUrl.includes("api.c000")) {
            console.log("Retrying fullscreen with login URL");
            config.baseUrl = "https://login.eagleeyenetworks.com";
            await this.eenFullscreenPlayer.start(config);
          } else {
            throw sdkError;
          }
        }
      } catch (error) {
        console.error("Failed to initialize fullscreen player:", error);
        this.playerError = "Failed to initialize video player";
        this.fullscreenLoading = false;
      }
    },

    setupVideoFrameCleanup(video) {
      // Store reference to original requestVideoFrameCallback if it exists
      if (video.requestVideoFrameCallback) {
        const originalCallback = video.requestVideoFrameCallback.bind(video);

        video.requestVideoFrameCallback = (callback) => {
          return originalCallback((now, metadata) => {
            try {
              return callback(now, metadata);
            } finally {
              // Ensure frames are properly handled
              if (metadata && typeof metadata.close === "function") {
                metadata.close();
              }
            }
          });
        };
      }

      // Add frame cleanup on pause/play events
      video.addEventListener("pause", () => this.cleanupVideoFrames(video));
      video.addEventListener("seeked", () => this.cleanupVideoFrames(video));

      // Cleanup frames when video is removed from DOM
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.removedNodes.forEach((node) => {
            if (node === video) {
              this.cleanupVideoFrames(video);
              observer.disconnect();
            }
          });
        });
      });

      if (video.parentNode) {
        observer.observe(video.parentNode, { childList: true });
      }
    },

    cleanupVideoFrames(video) {
      try {
        // Force garbage collection of any pending video frames
        if (video && video.videoWidth && video.videoHeight) {
          // Pause and clear the video to release frames
          video.pause();
          video.currentTime = 0;

          // Clear any pending frame callbacks
          if (video._frameCallbackId) {
            video.cancelVideoFrameCallback(video._frameCallbackId);
            video._frameCallbackId = null;
          }
        }
      } catch (error) {
        console.warn("Error cleaning up video frames:", error);
      }
    },

    async cleanupFullscreenPlayer() {
      if (this.eenFullscreenPlayer) {
        try {
          // Fix: Cleanup frames before stopping player
          if (this.currentVideoDevice) {
            const container = document.getElementById(
              `een_fullscreen_${this.currentVideoDevice.externalId}`
            );
            if (container) {
              const video = container.querySelector("video");
              if (video) {
                this.cleanupVideoFrames(video);
              }
            }
          }

          await this.eenFullscreenPlayer.stop();
          this.eenFullscreenPlayer = null;
          console.log("Fullscreen player cleaned up");
        } catch (error) {
          console.error("Error stopping EEN fullscreen player:", error);
        }
      }

      if (this.currentVideoDevice) {
        const container = document.getElementById(
          `een_fullscreen_${this.currentVideoDevice.externalId}`
        );
        if (container) {
          container.innerHTML = "";
        }
      }
    },

    // Update refreshEENPreview method
    async refreshEENPreview(deviceId) {
      try {
        if (this.eenPreviewPlayers[deviceId]) {
          // Fix: Cleanup frames before stopping
          const videoElement = document.getElementById(
            `een_preview_${deviceId}`
          );
          if (videoElement) {
            const video = videoElement.querySelector("video");
            if (video) {
              this.cleanupVideoFrames(video);
            }
          }

          await this.eenPreviewPlayers[deviceId].stop();
          delete this.eenPreviewPlayers[deviceId];
        }

        const videoElement = document.getElementById(`een_preview_${deviceId}`);
        if (videoElement) {
          videoElement.innerHTML = "";
        }

        setTimeout(async () => {
          await this.createEENPreview(deviceId);
        }, 500);
      } catch (error) {
        console.error(
          `Failed to refresh preview for device ${deviceId}:`,
          error
        );
      }
    },

    async retryFullscreenPlayer() {
      this.playerError = null;
      await this.cleanupFullscreenPlayer();
      this.$nextTick(async () => {
        await this.initializeFullscreenPlayer();
      });
    },

    cancel() {
      window.removeEventListener("message", this.handleIframeMessage);
      this.showIframe = false;
    },

    async fetchAuthenticationStatus() {
      try {
        const response = await this.$apollo.query({
          query: EEN_AUTH_STATUS,
          variables: {
            organization_id:
              this.currentFacilityId || this.currentVideoDevice?.facilityId,
          },
        });
        const authStatusData = response.data.eenAuthStatus;

        if (authStatusData.accessToken) {
          this.authStatus.value = true;
          this.authStatus.accessToken = authStatusData.accessToken;
          this.showIframe = true;

          this.$nextTick(() => {
            setTimeout(() => {
              this.setupIframeListener();
            }, 500);
          });
        }
      } catch (error) {
        console.error(error);
      }
    },

    setupIframeListener() {
      const iframe = document.getElementById("sd_vms_iframe");
      if (iframe) {
        this.handleIframeLoad();
      } else {
        setTimeout(() => {
          this.setupIframeListener();
        }, 300);
      }
    },

    handleIframeLoad() {
      const iframe = document.getElementById("sd_vms_iframe");
      const iframeWindow = iframe?.contentWindow;
      if (iframeWindow) {
        window.removeEventListener("message", this.handleIframeMessage);
        window.addEventListener("message", this.handleIframeMessage);

        this.sendTokenToIframe();
      } else {
        console.error("Iframe contentWindow is null");
      }
    },

    sendTokenToIframe() {
      const iframe = document.getElementById("sd_vms_iframe");
      if (iframe && iframe.contentWindow) {
        setTimeout(() => {
          iframe.contentWindow.postMessage(
            {
              type: "een-token",
              token: this.authStatus.accessToken,
            },
            "https://iframe.eagleeyenetworks.com/"
          );
        }, 500);

        setTimeout(() => {
          iframe.contentWindow.postMessage(
            {
              type: "een-token",
              token: this.authStatus.accessToken,
            },
            "https://iframe.eagleeyenetworks.com/"
          );
        }, 2000);
      }
    },

    handleIframeMessage(event) {
      if (
        event.data === "een-iframe-loaded" ||
        event.data === "een-iframe-token-expired"
      ) {
        const iframe = document.getElementById("sd_vms_iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: "een-token",
              token: this.authStatus.accessToken,
            },
            "https://iframe.eagleeyenetworks.com/"
          );
        } else {
          console.error("Iframe or iframe contentWindow is null");
        }
      }
    },

    getCurrentTime() {
      return this.currentTime;
    },

    updateCurrentTime() {
      this.currentTime = new Date().toLocaleTimeString();
    },
  },
};
</script>
<style scoped>
/* EEN SDK specific styles */
.een-video-preview {
  width: 100%;
  height: 100%;
  background-color: #000;
  position: relative;
}

.een-player-container {
  width: 100%;
  height: 100%;
  background-color: #000;
}

.een-fullscreen-container {
  width: 100%;
  height: 100%;
  background-color: #000;
}

.video-card {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  overflow: hidden;
}

.video-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.video-container {
  position: relative;
  overflow: hidden;
}

.video-loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.video-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 8;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.video-container {
  position: relative;
  overflow: hidden;
  border-radius: 12px; /* Match card border radius */
}

.rounded-lg {
  border-radius: 12px !important;
}

/* New time overlay at bottom left */
.video-time-overlay {
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 10;
  background: #0000;
  padding: 0px 4px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
}

.video-time-overlay .camera-time {
  font-size: 11px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.header-left {
  color: white;
}

.camera-time {
  font-size: 11px;
  font-weight: bold;
  margin-bottom: 2px;
}

.camera-name-container {
  display: flex;
  align-items: center;
}

.camera-name {
  font-size: 13px;
  font-weight: 500;
}

.info-icon {
  cursor: pointer;
  transition: opacity 0.2s;
}

.info-icon:hover {
  opacity: 0.7;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.fullscreen-btn {
  background-color: rgba(0, 0, 0, 0.6) !important;
}

.live-preview-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.preview-controls {
  position: absolute;
  bottom: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 15;
}

.video-card:hover .preview-controls {
  opacity: 1;
}

.refresh-btn {
  background-color: rgba(0, 0, 0, 0.6) !important;
}

.play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 12;
}

.video-card:hover .play-overlay {
  opacity: 0.9;
}

.play-button {
  background-color: rgba(0, 0, 0, 0.6) !important;
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: white;
  text-align: center;
  background-color: #1a1a1a;
}

.error-text {
  margin: 8px 0;
}

/* Fullscreen styles - matching VideoGridLive exactly */
.fullscreen-container {
  height: calc(100vh - 64px);
  background-color: #000;
}

.video-player-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.video-view-container {
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  background-color: #000;
}

.fullscreen-loading,
.fullscreen-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
}

.camera-nav-buttons {
  display: flex;
  align-items: center;
}

/* Tooltip styling */
.camera-tooltip {
  text-align: left;
  font-size: 12px;
  line-height: 1.4;
}

.camera-tooltip div {
  margin-bottom: 2px;
}

.camera-tooltip div:last-child {
  margin-bottom: 0;
}

/* Responsive grid adjustments */
@media (max-width: 599px) {
  .camera-name {
    font-size: 12px;
  }

  .camera-time {
    font-size: 10px;
  }
}

/* Iframe container styles */
.iframe-container {
  width: 100% !important;
  height: 100% !important;
  position: relative !important;
  background-color: #000;
}

/* Fullscreen iframe styles */
.fullscreen-iframe {
  width: 100% !important;
  height: 100% !important;
  border: none !important;
  background-color: #000;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
}

/* Ensure the dialog takes full space */
.v-dialog--fullscreen {
  overflow: hidden !important;
}

.v-dialog--fullscreen .v-card {
  height: 100vh !important;
  max-height: 100vh !important;
  overflow: hidden !important;
}
</style>
